import type React from 'react';
import { useState } from 'react';

import { Text } from '@change/ds-core-ui';
import { Checkbox } from '@change/ds-forms';
import { CaretDownIcon, CaretUpIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core/react/i18n';

import { ActionButton } from '../ActionButton';

type TaskHeaderProps = Readonly<{
	title: string;
	isFirstActive: boolean;
	isExpanded: boolean;
	primaryActionText: string;
	onToggleExpand: () => void;
	onCheckboxChange?: () => Promise<void>;
}>;

const getExpandAriaLabel = (isExpanded: boolean, translate: (key: string) => string): string => {
	return isExpanded
		? translate('corgi.petition.starter_dashboard_v2.dynamic_tasks.task.collapse_details_aria')
		: translate('corgi.petition.starter_dashboard_v2.dynamic_tasks.task.expand_details_aria');
};

/**
 * Renders the task header with checkbox, title, action button, and expand/collapse toggle.
 */
export const TaskHeader: React.FC<TaskHeaderProps> = ({
	title,
	isFirstActive,
	isExpanded,
	primaryActionText,
	onToggleExpand,
	onCheckboxChange,
}) => {
	const { translate } = useI18n();
	const [isChecked, setIsChecked] = useState(false);
	const ariaLabel = getExpandAriaLabel(isExpanded, translate);

	const handleCheckboxChange = (newValue: boolean) => {
		if (!newValue || !onCheckboxChange) return;
		setIsChecked(true);
		void onCheckboxChange();
	};

	return (
		<div className="flex items-center gap-5" data-qa="dynamic-task-header">
			<div className="shrink-0">
				<Checkbox
					name={`dynamic-task-checkbox-${title}`}
					isReadOnly={isChecked}
					onChange={handleCheckboxChange}
					aria-label={title}
				/>
			</div>
			<div className="flex min-w-0 flex-1 items-center">
				<Text variant="body" className="text-size-body-bold" as="p" data-qa="dynamic-task-title">
					{title}
				</Text>
			</div>
			<div className="flex shrink-0 items-center gap-5">
				<ActionButton
					isFirstActive={isFirstActive}
					primaryActionText={primaryActionText}
					className="hidden w-44 md:inline-flex"
				/>
				<button
					type="button"
					onClick={onToggleExpand}
					className="bg-background-overlay flex items-center justify-center rounded-full p-1.5"
					aria-label={ariaLabel}
					data-qa="dynamic-task-expand-toggle"
				>
					{isExpanded ? <CaretUpIcon size={24} /> : <CaretDownIcon size={24} />}
				</button>
			</div>
		</div>
	);
};
